import { useState, useEffect } from 'react';
import { ref, get, set, onValue } from 'firebase/database';
import { database } from '../firebase/firebase';
import { UserProfile, BankInfo } from '../types';
import { useAuth } from './useAuth';
import { useEventUser } from './useEventUser';
import { getEventSelection } from '../utils/eventSelections';
import { syncAccountNameAcrossEvents } from '../utils/syncDisplayName';

/**
 * Where a new display name applies:
 * - `event`   — this bill only (written to eventUsers, and to the event meta when
 *               the current user owns the event, which is the only case Firebase allows).
 * - `account` — the account name, carried into every event still using the old one.
 */
export type DisplayNameScope = 'event' | 'account';

interface UnifiedProfileData {
    displayName: string;
    email?: string;
    createdAt?: number;
    lastAccess?: number;
    bankInfo?: BankInfo;
    isAnonymous: boolean;
    isOwner: boolean;
    userId: string;
}

interface PermissionFlags {
    canEditDisplayName: boolean;
    canEditBankInfo: boolean;
    canViewProfile: boolean;
}

export const useUnifiedProfile = (eventId?: string, forceUserId?: string) => {
    const { user } = useAuth();
    const { updateEventUserDisplayName, updateEventUserBankInfo } = useEventUser();
    const [profileData, setProfileData] = useState<UnifiedProfileData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [eventName, setEventName] = useState<string>('');
    const [isCurrentUserOwner, setIsCurrentUserOwner] = useState(false);
    // The account-level name, kept live so the event profile can offer
    // "back to my account name" and know when the event name is a custom one.
    const [accountDisplayName, setAccountDisplayName] = useState<string>('');

    // Determine current user context
    const getCurrentUserId = (): string | null => {
        if (forceUserId) return forceUserId;
        if (user) return user.uid;
        if (eventId) return getEventSelection(eventId);
        return null;
    };

    const currentUserId = getCurrentUserId();
    const isEventContext = !!eventId;
    const isAnonymousUser = currentUserId?.startsWith('~u_') || false;

    // **ENHANCED PERMISSIONS LOGIC**
    const getPermissions = (): PermissionFlags => {
        // If not in event context, only logged users can edit their own profile
        if (!isEventContext) {
            const isOwnProfile = user && currentUserId === user.uid;
            return {
                canViewProfile: true,
                canEditDisplayName: Boolean(isOwnProfile),
                canEditBankInfo: Boolean(isOwnProfile)
            };
        }

        // **EVENT CONTEXT PERMISSIONS**
        const isViewingSelectedParticipant = !forceUserId || (
            !user && forceUserId === getEventSelection(eventId!)
        );
        const isLoggedUserViewingOwnProfile = user && user.uid === currentUserId;
        const isLoggedUserViewingOther = user && forceUserId && forceUserId !== user.uid;

        // **Fix: Ensure isOwnerProfile is always boolean**
        const isOwnerProfile = profileData?.isOwner === true;

        // **NEW: Enhanced permission rules with owner privileges:**
        let canEdit = false;

        if (isCurrentUserOwner) {
            // **OWNER CAN EDIT ALL PARTICIPANTS** (except other owners)
            if (!isOwnerProfile) {
                // Can edit any non-owner participant
                canEdit = true;
            } else {
                // Can only edit if it's their own owner profile
                canEdit = Boolean(user && user.uid === currentUserId);
            }
        } else if (!user && isViewingSelectedParticipant && !isOwnerProfile) {
            // Anonymous user editing their selected participant (not owner)
            canEdit = true;
        } else if (user && isLoggedUserViewingOwnProfile) {
            // Logged user editing their own profile
            canEdit = true;
        } else if (user && !isLoggedUserViewingOther && isViewingSelectedParticipant && !isOwnerProfile) {
            // Logged user editing selected participant when not viewing other's profile
            canEdit = true;
        }

        return {
            canViewProfile: true,
            canEditDisplayName: canEdit,
            canEditBankInfo: canEdit
        };
    };

    // **Call getPermissions after profileData is available**
    const permissions = profileData ? getPermissions() : {
        canViewProfile: true,
        canEditDisplayName: false,
        canEditBankInfo: false
    };

    useEffect(() => {
        if (!user) {
            setAccountDisplayName('');
            return;
        }
        const accountNameRef = ref(database, `users/${user.uid}/displayName`);
        return onValue(accountNameRef, snapshot => {
            setAccountDisplayName(snapshot.val() || user.displayName || '');
        });
    }, [user]);

    useEffect(() => {
        const fetchProfileData = async () => {
            if (!currentUserId) {
                setError('No user context available');
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const isAnonymous = currentUserId.startsWith('~u_');
                let isOwner = false;

                // Get event info if in event context
                if (eventId) {
                    const eventRef = ref(database, `events/${eventId}/meta`);
                    const eventSnapshot = await get(eventRef);
                    if (eventSnapshot.exists()) {
                        const eventMeta = eventSnapshot.val();
                        setEventName(eventMeta.name || 'Unknown Event');
                        // Events created by the app only carry `createdBy`; `ownerId`
                        // exists on older ones. Reading just one of them made every
                        // owner look like a plain participant.
                        const ownerId = eventMeta.ownerId || eventMeta.createdBy;
                        isOwner = ownerId === currentUserId;

                        // **Check if current logged user is the event owner**
                        setIsCurrentUserOwner(Boolean(user && ownerId === user.uid));
                    }
                }

                // **UNIFIED DATA FETCHING: Always prioritize eventUsers in event context**
                if (eventId) {
                    // Try eventUsers first (for both anonymous and registered users)
                    const eventUserRef = ref(database, `eventUsers/${eventId}/${currentUserId}`);
                    const eventUserSnapshot = await get(eventUserRef);

                    if (eventUserSnapshot.exists()) {
                        const eventUser = eventUserSnapshot.val();
                        setProfileData({
                            displayName: eventUser.displayName,
                            email: eventUser.email,
                            createdAt: eventUser.createdAt,
                            lastAccess: eventUser.lastAccess,
                            bankInfo: eventUser.bankInfo,
                            isAnonymous: eventUser.isAnonymous || isAnonymous,
                            isOwner,
                            userId: currentUserId
                        });
                    } else {
                        // Fallback for anonymous users to old structure
                        if (isAnonymous) {
                            const anonRef = ref(database, `anonymousUsers/${eventId}/${currentUserId}`);
                            const anonSnapshot = await get(anonRef);

                            if (anonSnapshot.exists()) {
                                const anonymousUser = anonSnapshot.val();
                                setProfileData({
                                    displayName: anonymousUser.displayName,
                                    createdAt: anonymousUser.createdAt,
                                    lastAccess: anonymousUser.lastAccess,
                                    bankInfo: anonymousUser.bankInfo,
                                    isAnonymous: true,
                                    isOwner,
                                    userId: currentUserId
                                });
                            } else {
                                setError('Anonymous user not found');
                            }
                        } else {
                            // Registered user not in eventUsers yet - get basic info from users
                            const userRef = ref(database, `users/${currentUserId}`);
                            const userSnapshot = await get(userRef);

                            if (userSnapshot.exists()) {
                                const userProfile: UserProfile = userSnapshot.val();
                                setProfileData({
                                    displayName: userProfile.displayName || 'User',
                                    email: userProfile.email || undefined,
                                    createdAt: userProfile.createdAt,
                                    bankInfo: undefined,
                                    isAnonymous: false,
                                    isOwner,
                                    userId: currentUserId
                                });
                            } else {
                                setError('User not found');
                            }
                        }
                    }
                } else {
                    // Non-event context - get from users table
                    const userRef = ref(database, `users/${currentUserId}`);
                    const snapshot = await get(userRef);

                    if (snapshot.exists()) {
                        const userProfile: UserProfile = snapshot.val();
                        setProfileData({
                            displayName: userProfile.displayName || 'User',
                            email: userProfile.email || undefined,
                            createdAt: userProfile.createdAt,
                            bankInfo: userProfile.bankInfo || undefined,
                            isAnonymous: false,
                            isOwner: false,
                            userId: currentUserId
                        });
                    } else {
                        setError('User not found');
                    }
                }

            } catch (error) {
                console.error('Error fetching profile data:', error);
                setError('Failed to load profile data');
            } finally {
                setLoading(false);
            }
        };

        fetchProfileData();
    }, [currentUserId, eventId, user]);


// **UNIFIED UPDATE FUNCTIONS**
    const updateDisplayName = async (
        newDisplayName: string,
        scope: DisplayNameScope = eventId ? 'event' : 'account'
    ) => {
        if (!currentUserId || !permissions.canEditDisplayName) {
            throw new Error('Permission denied - cannot edit this profile');
        }

        // Only the account holder can change the account name; renaming someone
        // else (an owner editing a participant) is always event-scoped.
        const canWriteAccount = Boolean(user && user.uid === currentUserId);
        const effectiveScope: DisplayNameScope = scope === 'account' && canWriteAccount ? 'account' : 'event';

        try {
            if (effectiveScope === 'account') {
                const previousName = accountDisplayName;
                await set(ref(database, `users/${user!.uid}/displayName`), newDisplayName);
                await syncAccountNameAcrossEvents(user!.uid, newDisplayName, previousName, eventId);
            } else if (eventId) {
                await updateEventUserDisplayName(eventId, currentUserId, newDisplayName);

                // `events/<id>/meta` only accepts writes from the event owner —
                // for everyone else the eventUsers entry above is what the bill reads.
                if (isCurrentUserOwner) {
                    const eventMetaRef = ref(database, `events/${eventId}/meta/participants/${currentUserId}`);
                    await set(eventMetaRef, newDisplayName);
                }
            } else {
                throw new Error('Cannot change this name without an account');
            }

            setProfileData(prev => prev ? {
                ...prev,
                displayName: newDisplayName
            } : null);
        } catch (error) {
            console.error('Error updating display name:', error);
            throw error;
        }
    };

    const updateBankInfo = async (bankInfo: BankInfo) => {
        if (!currentUserId || !permissions.canEditBankInfo) {
            throw new Error('Permission denied - cannot edit this profile');
        }

        try {
            if (eventId) {
                // Always update in eventUsers for event context
                await updateEventUserBankInfo(eventId, currentUserId, bankInfo);
            } else {
                // Update in users table for non-event context
                const bankInfoRef = ref(database, `users/${currentUserId}/bankInfo`);
                await set(bankInfoRef, bankInfo);
            }

            // Update local state
            setProfileData(prev => prev ? { ...prev, bankInfo } : null);
        } catch (error) {
            console.error('Error updating bank info:', error);
            throw error;
        }
    };

    // **Helper function to check if viewing self in current context**
    const isViewingSelf = () => {
        if (!user && eventId) {
            // Anonymous user - check if viewing selected participant
            const selectedUserId = getEventSelection(eventId);
            return !forceUserId || forceUserId === selectedUserId;
        }

        if (user) {
            // Logged user - check if viewing own profile
            return !forceUserId || forceUserId === user.uid;
        }

        return false;
    };

    return {
        // Data
        profileData,
        eventName,
        loading,
        error,

        // Context flags
        isEventContext,
        isViewingSelf: isViewingSelf(),
        isAnonymousUser,
        currentUserId,
        isCurrentUserOwner,
        accountDisplayName,

        // Permissions
        permissions,

        // Actions
        updateDisplayName,
        updateBankInfo
    };
};